/**
 * Impact Note — Add / Edit modal.
 * Form parity with Angular `ImpactNotesEditComponent`:
 *   title (required), description (required), daterange → `from_date` / `to_date` (required) via
 *   `AdvancedDateRangePicker` (six ngx presets + three calendar years like `import-data` /
 *   `getAngularSystemAnnouncementPresets`, two months, Apply).
 */

import { useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { format } from 'date-fns'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import modalStyles from '@/features/application/styles/modals.module.css'
import {
  AdvancedDateRangePicker,
  getAngularSystemAnnouncementPresets,
} from '@/components/ui/AdvancedDateRangePicker'
import { impactNoteFormSchema, type ImpactNoteFormValues } from '../types/schemas'
import type { ImpactNoteEditModalProps } from '../types'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'

function unixToDateString(unixSeconds: number | null | undefined): string {
  if (!unixSeconds) return ''
  const date = new Date(unixSeconds * 1000)
  if (Number.isNaN(date.getTime())) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Local calendar day from `YYYY-MM-DD` (avoids UTC shift from `new Date(ymd)`). */
function ymdStringToDate(ymd: string): Date | null {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(y, m - 1, d)
}

export function ImpactNoteEditModal({
  isOpen,
  note,
  submitting,
  onClose,
  onSubmit,
}: ImpactNoteEditModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<ImpactNoteFormValues>({
    resolver: zodResolver(impactNoteFormSchema),
    mode: 'onChange',
    defaultValues: {
      title: '',
      description: '',
      from_date: '',
      to_date: '',
    },
  })

  useEffect(() => {
    if (!isOpen) return
    reset({
      title: note?.title ?? '',
      description: note?.description ?? '',
      from_date: unixToDateString(note?.from_date),
      to_date: unixToDateString(note?.to_date),
    })
  }, [isOpen, note, reset])

  // eslint-disable-next-line react-hooks/incompatible-library -- react-hook-form watch() is intentionally unmemoizable
  const fromDate = watch('from_date')
  const toDate = watch('to_date')

  const impactNoteDatePresets = useMemo(() => getAngularSystemAnnouncementPresets(), [])

  const handleDateRangeChange = useCallback(
    (start: Date | null, end: Date | null) => {
      setValue('from_date', start ? format(start, 'yyyy-MM-dd') : '', {
        shouldValidate: true,
        shouldDirty: true,
      })
      setValue('to_date', end ? format(end, 'yyyy-MM-dd') : '', { shouldValidate: true, shouldDirty: true })
    },
    [setValue],
  )

  const internalSubmit = (values: ImpactNoteFormValues) => {
    onSubmit(
      {
        title: values.title.trim(),
        description: values.description.trim(),
        from_date: values.from_date,
        to_date: values.to_date,
      },
      note?.id,
    )
  }

  const title = note?.id ? 'Edit Impact Notes Details' : 'Add Impact Notes Details'

  return (
    <OnScreenModal isOpen={isOpen} title={title} onClose={onClose} maxWidth="max-w-3xl">
      <form
        onSubmit={handleSubmit(internalSubmit)}
        className="min-w-0 space-y-4 p-2 sm:p-4 md:p-6"
      >
        <div>
          <label htmlFor="impact-note-title" className="block text-sm font-medium text-slate-700 mb-1">
            Title
          </label>
          <input
            id="impact-note-title"
            type="text"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            {...register('title')}
          />
          {errors.title && (
            <p className={modalStyles.formFieldError} role="alert">
              {errors.title.message}
            </p>
          )}
        </div>

        <div
          className="space-y-1"
          role="group"
          aria-labelledby="impact-note-daterange-label"
          data-testid="impact-note-edit-date-range"
        >
          <label
            id="impact-note-daterange-label"
            className="block text-sm font-medium text-slate-700 mb-1"
          >
            From - To Date
          </label>
          <div className="relative max-w-full min-w-0 overflow-x-auto pb-1 sm:max-w-2xl">
            <AdvancedDateRangePicker
              startDate={ymdStringToDate(fromDate || '')}
              endDate={ymdStringToDate(toDate || '')}
              onChange={handleDateRangeChange}
              placeholder="Select Date Range"
              customPresets={impactNoteDatePresets}
              showApplyButton
              applyButtonPlacement="top"
              monthsShown={2}
              // Side-by-side months: shorter than stacked, fits modals and matches common range pickers.
              stackMonthsVertically={false}
              displayRangeFormat="angularMmddyyyy"
              presetsSingleColumn
              dropdownPortalToBody
            />
          </div>
          {(errors.from_date || errors.to_date) && (
            <p className={modalStyles.formFieldError} role="alert">
              {errors.from_date?.message || errors.to_date?.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="impact-note-description"
            className="block text-sm font-medium text-slate-700 mb-1"
          >
            Description
          </label>
          <textarea
            id="impact-note-description"
            rows={5}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            {...register('description')}
          />
          {errors.description && (
            <p className={modalStyles.formFieldError} role="alert">
              {errors.description.message}
            </p>
          )}
        </div>

        <div className="flex flex-wrap justify-center gap-2 border-t border-slate-200 pt-4 mt-4">
          <button
            type="submit"
            disabled={!isDirty || submitting}
            className={cn(primaryEntityActionButtonClass, "w-full sm:w-auto")}
          >
            {submitting ? 'Saving...' : 'Submit'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={cn(secondaryEntityActionButtonClass, "w-full sm:w-auto")}
          >
            Cancel
          </button>
        </div>
      </form>
    </OnScreenModal>
  )
}
